import { useEffect } from 'react';
import { type MockQuotation } from '@/mocks/billing';
import { formatINR } from '@/utils/format';

interface PrintQuotationProps {
  quotation: MockQuotation;
  onClose: () => void;
}

export default function PrintQuotation({ quotation, onClose }: PrintQuotationProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handlePrint = () => {
    window.print();
  };

  const qt = quotation;

  return (
    <>
      {/* Screen preview overlay */}
      <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto pt-8 pb-8">
        <div className="bg-white w-[210mm] min-h-[297mm] shadow-2xl relative print:shadow-none print:fixed print:inset-0 print:w-auto print:h-auto print:overflow-visible print:bg-white print:z-[9999]">
          {/* Toolbar (screen only) */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between print:hidden z-10">
            <span className="text-sm font-semibold text-slate-700">Print Preview — {qt.quotationNumber}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-printer-line" /> Print
              </button>
              <button
                onClick={onClose}
                className="flex items-center gap-1.5 h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-close-line" /> Close
              </button>
            </div>
          </div>

          {/* Print Document */}
          <div className="p-10 print:p-0">
            {/* Company Header */}
            <div className="border-b-2 border-slate-800 pb-4 mb-5">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900 tracking-tight">InvenPro Manufacturing Pvt Ltd</h1>
                  <p className="text-sm text-slate-600 mt-1">
                    Plot 88, MIDC Bhosari, Pune 411026, Maharashtra
                  </p>
                  <p className="text-sm text-slate-600">
                    GSTIN: 27AABCI1234E1Z5 &nbsp;|&nbsp; Phone: +91-20-12345678
                  </p>
                </div>
                <div className="text-right">
                  <h2 className="text-xl font-bold text-slate-900 tracking-wide">QUOTATION</h2>
                  <p className="text-xs text-slate-500 mt-1">Original Copy</p>
                </div>
              </div>
            </div>

            {/* QT Info + Customer */}
            <div className="flex justify-between mb-6">
              <div className="max-w-[55%]">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">To:</h3>
                <p className="text-sm font-bold text-slate-800">{qt.customerName}</p>
                {qt.customerGSTIN && <p className="text-xs text-slate-600 mt-0.5">GSTIN: {qt.customerGSTIN}</p>}
                {qt.customerPhone && <p className="text-xs text-slate-600">Phone: {qt.customerPhone}</p>}
                {qt.customerEmail && <p className="text-xs text-slate-600">{qt.customerEmail}</p>}
              </div>
              <div className="text-right text-sm">
                <div className="mb-1">
                  <span className="text-xs text-slate-500">QT No:</span>{' '}
                  <span className="font-bold text-slate-800">{qt.quotationNumber}</span>
                </div>
                <div className="mb-1">
                  <span className="text-xs text-slate-500">Date:</span>{' '}
                  <span className="text-slate-700">{new Date(qt.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div className="mb-1">
                  <span className="text-xs text-slate-500">Valid Until:</span>{' '}
                  <span className="text-slate-700">{new Date(qt.validUntil).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Ref:</span>{' '}
                  <span className="text-slate-700">Your Enquiry</span>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <table className="w-full text-sm border-collapse mb-5">
              <thead>
                <tr className="border-y-2 border-slate-800">
                  <th className="text-center py-2 px-2 text-xs font-bold text-slate-700 uppercase w-8">#</th>
                  <th className="text-left py-2 px-2 text-xs font-bold text-slate-700 uppercase">Item</th>
                  <th className="text-center py-2 px-2 text-xs font-bold text-slate-700 uppercase">HSN</th>
                  <th className="text-right py-2 px-2 text-xs font-bold text-slate-700 uppercase">Qty</th>
                  <th className="text-center py-2 px-2 text-xs font-bold text-slate-700 uppercase">Unit</th>
                  <th className="text-right py-2 px-2 text-xs font-bold text-slate-700 uppercase">Rate</th>
                  <th className="text-center py-2 px-2 text-xs font-bold text-slate-700 uppercase">Disc%</th>
                  <th className="text-right py-2 px-2 text-xs font-bold text-slate-700 uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {qt.items.map((item, idx) => (
                  <tr key={item.id} className="border-b border-slate-200">
                    <td className="text-center py-2 px-2 text-slate-600">{idx + 1}</td>
                    <td className="py-2 px-2">
                      <span className="font-medium text-slate-800">{item.itemName}</span>
                      {item.description && <span className="text-xs text-slate-500 block">{item.description}</span>}
                    </td>
                    <td className="text-center py-2 px-2 text-slate-600 text-xs">{item.hsnCode}</td>
                    <td className="text-right py-2 px-2 text-slate-700">{item.qty}</td>
                    <td className="text-center py-2 px-2 text-slate-500 text-xs">{item.unit}</td>
                    <td className="text-right py-2 px-2 text-slate-700">{formatINR(item.rate)}</td>
                    <td className="text-center py-2 px-2 text-slate-600">{item.discountPercent > 0 ? `${item.discountPercent}%` : '—'}</td>
                    <td className="text-right py-2 px-2 font-medium text-slate-800">{formatINR(item.taxableAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between border-b border-slate-200 pb-1">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-medium text-slate-800">{formatINR(qt.subtotal)}</span>
                </div>
                {qt.discountAmount > 0 && (
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="text-slate-600">Discount</span>
                    <span className="font-medium text-red-600">-{formatINR(qt.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between border-b border-slate-200 pb-1">
                  <span className="text-slate-600">Taxable Amount</span>
                  <span className="font-medium text-slate-800">{formatINR(qt.taxableAmount)}</span>
                </div>
                {qt.items.some((i) => i.igst > 0) ? (
                  <div className="flex justify-between border-b border-slate-200 pb-1">
                    <span className="text-slate-600">IGST ({qt.items[0]?.taxRate ?? 18}%)</span>
                    <span className="font-medium text-slate-800">{formatINR(qt.taxAmount)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between border-b border-slate-200 pb-1">
                      <span className="text-slate-600">CGST ({(qt.items[0]?.taxRate ?? 18) / 2}%)</span>
                      <span className="font-medium text-slate-800">{formatINR(qt.taxAmount / 2)}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-200 pb-1">
                      <span className="text-slate-600">SGST ({(qt.items[0]?.taxRate ?? 18) / 2}%)</span>
                      <span className="font-medium text-slate-800">{formatINR(qt.taxAmount / 2)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between pt-1">
                  <span className="font-bold text-slate-900 text-base">TOTAL</span>
                  <span className="font-bold text-slate-900 text-base">{formatINR(qt.totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="border-t-2 border-slate-300 pt-4 text-xs text-slate-600 space-y-2">
              {qt.paymentTerms && (
                <p><span className="font-semibold text-slate-700">Payment Terms:</span> {qt.paymentTerms}</p>
              )}
              {qt.deliveryTerms && (
                <p><span className="font-semibold text-slate-700">Delivery Terms:</span> {qt.deliveryTerms}</p>
              )}
              <p>This quotation is valid until {new Date(qt.validUntil).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
              {qt.termsAndConditions && (
                <div className="mt-2">
                  <p className="font-semibold text-slate-700 mb-1">Terms &amp; Conditions:</p>
                  <p className="whitespace-pre-line">{qt.termsAndConditions}</p>
                </div>
              )}
            </div>

            {/* Signature */}
            <div className="mt-10 flex justify-end">
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-800">For InvenPro Manufacturing Pvt Ltd</p>
                <div className="mt-8 border-t border-slate-400 pt-1">
                  <p className="text-xs text-slate-500">Authorised Signatory</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .fixed.inset-0.z-50 > .bg-white,
          .fixed.inset-0.z-50 > .bg-white * { visibility: visible; }
          .fixed.inset-0.z-50 {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </>
  );
}